// src/app/interceptors/csrf-interceptor.ts
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { Observable, catchError, of, switchMap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';

@Injectable()
export class CsrfInterceptor implements HttpInterceptor {
  csrfToken: string | null = null;

  originalRequest!: HttpRequest<any>;

  constructor(
    private _httpClient: HttpClient,
    private _authService: AuthService,
    private _cookieService: CookieService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // if assets are requestet, send the request without modification.
    if (
      this.isStaticResource(req.url) ||
      req.url.startsWith(environment.cms_url) ||
      req.url.endsWith('/refresh-csrf')
    ) {
      return next.handle(req);
    }
    // save the original request after the above if to only save the relevant requests
    this.originalRequest = req;

    // Get CSRF token from cookies or request a new one if not present
    return this.getCsrfTokenFromCookies().pipe(
      switchMap((token) => {
        if (!token) {
          // Request new CSRF token if it does not exist
          return this.requestNewCsrfToken().pipe(
            switchMap((newToken) => {
              this.csrfToken = newToken;
              const clonedRequest = req.clone({
                setHeaders: {
                  'x-almanjobs-xsrf-token': newToken,
                },
              });
              return next
                .handle(clonedRequest)
                .pipe(
                  catchError((error) => this.Errorhandler(error, req, next)),
                );
            }),
          );
        } else {
          this.csrfToken = token;
          const clonedRequest = req.clone({
            setHeaders: {
              'x-almanjobs-xsrf-token': token,
            },
          });
          return next.handle(clonedRequest).pipe(
            // if the token is invalid a new token will be requested
            catchError((error) => this.Errorhandler(error, req, next)),
          );
        }
      }),
    );
  }

  private Errorhandler(
    error: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (
      error.status === 403 &&
      error.error &&
      error.error.message === 'EBADCSRFTOKEN'
    ) {
      // If the CSRF token is invalid, refresh the token
      return this.requestNewCsrfToken().pipe(
        switchMap((newToken) => {
          this.csrfToken = newToken;
          const clonedRequest = this.originalRequest.clone({
            setHeaders: {
              'x-almanjobs-xsrf-token': newToken,
            },
          });
          return next.handle(clonedRequest).pipe(
            // if after requesting a new token we still receive an error then the user must be logged out
            catchError((error) => {
              if (
                error.status === 403 &&
                error.error &&
                error.error.message === 'EBADCSRFTOKEN'
              ) {
                this._authService.logout(true);
                return throwError(
                  () => new Error('EBADCSRFTOKEN' + error.message),
                );
              }
              return throwError(
                () => new Error('something went wrong. please login again'),
              );
            }),
          );
        }),
        // if the token cannot be fetched, then something is wrong with the server
        catchError((error) => {
          return throwError(
            () => new Error('something went wrong. please login again'),
          );
        }),
      );
    } else {
      // if the error from the server is something else than bad csrf then something is wrong with the server
      return throwError(
        () => error
      )
    }
  }

  // Helper method to get CSRF token from cookies
  private getCsrfTokenFromCookies(): Observable<string | null> {
    const csrfToken = this._cookieService.get('almanjobs-xsrf-token');
    return of(csrfToken ? csrfToken : null);
  }

  // Helper method to request a new CSRF token from the server
  private requestNewCsrfToken(): Observable<string> {
    return this._httpClient
      .get<{ csrfToken: string }>(`${environment.api_url}/refresh-csrf`, {
        withCredentials: true,
      })
      .pipe(
        switchMap((response) => {
          return of(response.csrfToken);
        }),
      );
  }

  // Helper method to check if the URL is for a static resource
  private isStaticResource(url: string): boolean {
    const staticExtensions = [
      '.js',
      '.css',
      '.png',
      '.jpg',
      '.jpeg',
      '.gif',
      '.svg',
      '.json',
    ];
    return staticExtensions.some((ext) => url.includes(ext));
  }
}
